import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import {
  Archive,
  ArchiveRestore,
  ArrowUpDown,
  Check,
  ChevronDown,
  Download,
  Loader2,
  RotateCcw,
  Search,
  Tag,
  Trash2,
  Upload,
  User,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { ouvrirConversation } from '@/lib/ouvrirConversation'
import { nomContact } from '@/lib/nomContact'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { initiales } from '@/components/ui/AvatarPersonne'
import { AddContactDialog } from './AddContactDialog'
import { ImportContactsDialog } from './ImportContactsDialog'
import { ContactProfile } from './ContactProfile'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useProfile } from '@/api/profile'
import {
  exportContactsCsv,
  paramsFiltres,
  useBulkContacts,
  useContactFacets,
  useContactsPaginated,
  type BulkContactsAction,
  type ContactFacet,
  type ContactsSort,
  type ContactsView,
} from '@/api/contacts'
import { useContactProperties, type ContactPropertyDef } from '@/api/contactProperties'
import { useCallActions } from '@/hooks/useCallActions'
import { useSelectedCallerId } from '@/hooks/useSelectedCallerId'
import type { Contact, ContactStatus } from '@/types/contacts'

const CLE_TRI = 'callizy_contacts_sort'
const STATUTS: ContactStatus[] = ['prospect', 'lead', 'client', 'inactive']
const COULEUR_STATUT: Record<ContactStatus, string> = {
  prospect: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  lead: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  client: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  inactive: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
}

function lireTri(): ContactsSort {
  try {
    const v = localStorage.getItem(CLE_TRI)
    return v === 'created' || v === 'name' || v === 'company' ? v : 'recent'
  } catch {
    return 'recent'
  }
}

/** Le nom de la colonne Nom : la personne, sinon l'entreprise, sinon le numéro. */
function nomLigne(c: Contact): string {
  const personne = `${c.first_name || ''} ${c.last_name || ''}`.trim()
  // L'entreprise a sa colonne : on ne la répète pas à côté d'un nom.
  return nomContact(
    { first_name: c.first_name, last_name: c.last_name, company_name: personne ? undefined : c.company_name },
    formatPhoneDisplay(c.phone_number),
  )
}

/**
 * Contacts = le carnet d'adresses : un tableau pleine largeur pour chercher,
 * filtrer, trier, importer, exporter et agir sur plusieurs contacts à la fois.
 * Un clic sur une ligne ouvre la fiche à droite ; la conversation, elle, vit
 * dans la Boîte de réception.
 */
export function ContactsTab() {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'en' ? enUS : fr
  const darkMode = useUIStore((s) => s.darkMode)
  const setProfilePanelCollapsed = useUIStore((s) => s.setProfilePanelCollapsed)
  const setProfileToEditPhone = useUIStore((s) => s.setProfileToEditPhone)

  // Import, export, archiving, deleting and the trash: managers only (the server checks too).
  const { data: profile } = useProfile()
  const estGestionnaire = profile?.role === 'MANAGER'

  const [addOpen, setAddOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const ouvrirImport = estGestionnaire ? () => setImportOpen(true) : undefined

  // --- Recherche, filtres, tri -------------------------------------------------
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(id)
  }, [search])
  const [tag, setTag] = useState('')
  const [status, setStatus] = useState('')
  const [source, setSource] = useState('')
  const [vue, setVue] = useState<ContactsView>('active')
  const corbeille = vue === 'deleted'
  const [tri, setTri] = useState<ContactsSort>(lireTri)
  const choisirTri = (v: ContactsSort) => {
    setTri(v)
    try {
      localStorage.setItem(CLE_TRI, v)
    } catch {
      /* navigation privée : le choix vaut pour la session */
    }
  }
  const filtresActifs = !!(debouncedSearch.trim() || tag || status || source)
  const effacerFiltres = () => {
    setSearch('')
    setDebouncedSearch('')
    setTag('')
    setStatus('')
    setSource('')
  }

  // The trash has no tag, status or source filter and keeps its own order (last deleted first).
  const filtres = corbeille ? {} : { tag, status, source }
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useContactsPaginated(
    debouncedSearch,
    vue,
    corbeille ? 'recent' : tri,
    filtres,
  )
  const contacts = useMemo(() => (data?.pages ?? []).flatMap((p) => p.contacts), [data])
  const total = data?.pages[0]?.total ?? 0

  const { data: facets } = useContactFacets()
  const { data: proprietes = [] } = useContactProperties()
  const colonnesProprietes = useMemo(
    () => [...proprietes].sort((a, b) => a.position - b.position).slice(0, 3),
    [proprietes],
  )

  // --- Sélection ---------------------------------------------------------------
  const [selection, setSelection] = useState<Set<string>>(new Set())
  // Gmail style: every contact of the filter, not only the loaded pages.
  const [toutLeFiltre, setToutLeFiltre] = useState(false)
  const viderSelection = () => {
    setSelection(new Set())
    setToutLeFiltre(false)
  }
  // Un filtre change la liste : la sélection repart de zéro.
  useEffect(() => {
    setSelection(new Set())
    setToutLeFiltre(false)
  }, [debouncedSearch, tag, status, source, vue])
  const toutCoche = contacts.length > 0 && contacts.every((c) => selection.has(c.phone_number))
  const nombreChoisi = toutLeFiltre ? total : selection.size
  const basculerTout = () => {
    const vider = toutLeFiltre || toutCoche
    setToutLeFiltre(false)
    setSelection(vider ? new Set() : new Set(contacts.map((c) => c.phone_number)))
  }
  const basculer = (phone: string) => {
    // Unchecking one row leaves "all of the filter".
    setToutLeFiltre(false)
    setSelection((prev) => {
      const next = new Set(prev)
      if (next.has(phone)) next.delete(phone)
      else next.add(phone)
      return next
    })
  }
  const etiquettesSelection = useMemo(() => {
    const set = new Set<string>()
    for (const c of contacts) if (selection.has(c.phone_number)) (c.tags || []).forEach((x) => set.add(x))
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [contacts, selection])

  const bulk = useBulkContacts()
  const [confirmerArchive, setConfirmerArchive] = useState(false)
  const [confirmerSuppression, setConfirmerSuppression] = useState(false)
  const agir = (action: BulkContactsAction, value?: string) => {
    const phones = [...selection].slice(0, 1000)
    if (!toutLeFiltre && !phones.length) return
    const cible = toutLeFiltre
      ? { all: true as const, filters: paramsFiltres(debouncedSearch, vue, filtres), expected: total }
      : { phones }
    bulk.mutate(
      { ...cible, action, value },
      {
        onSuccess: ({ updated = 0, queued }) => {
          if (queued) toast.success(t('contacts.table.bulk.queued', { count: queued }))
          else if (action === 'delete') toast.success(t('contacts.table.bulk.deleted', { count: updated }))
          else if (action === 'undelete') toast.success(t('contacts.table.bulk.undeleted', { count: updated }))
          else toast.success(t('contacts.table.bulk.done', { count: updated }))
          viderSelection()
        },
        onError: (err: unknown) => {
          const code = (err as { response?: { status?: number } })?.response?.status
          toast.error(
            code === 403
              ? t('contacts.table.bulk.forbidden', 'Action réservée au gestionnaire.')
              : code === 409
                ? t('contacts.table.bulk.changed', 'La liste a changé. Vérifiez le nombre et recommencez.')
                : t('sidebar.toasts.error', 'Erreur'),
          )
        },
      },
    )
  }

  // --- Export --------------------------------------------------------------------
  const [exportEnCours, setExportEnCours] = useState(false)
  const exporter = async () => {
    setExportEnCours(true)
    try {
      await exportContactsCsv(debouncedSearch, vue, filtres)
    } catch {
      toast.error(t('contacts.table.exportError', 'L’export a échoué.'))
    } finally {
      setExportEnCours(false)
    }
  }

  // --- Fiche ouverte à droite ----------------------------------------------------
  const [fichePhone, setFichePhone] = useState<string | null>(null)
  const { makeOutboundCall } = useCallActions()
  const selectedCallerId = useSelectedCallerId()

  // --- Page suivante au bas du tableau --------------------------------------------
  const sentinelRef = useRef<HTMLTableRowElement | null>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage()
      },
      { rootMargin: '300px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const libelleStatut = (v: string) => t(`contacts.statuses.${v}`, v)
  const libelleSource = (v: string) => t(`contacts.sources.${v}`, v)

  const trisLibelles: Record<ContactsSort, string> = {
    recent: t('contacts.sort.recent', 'Interactions récentes'),
    created: t('contacts.sort.created', 'Ajoutés récemment'),
    name: t('contacts.sort.name', 'Nom (A-Z)'),
    company: t('contacts.sort.company', 'Entreprise (A-Z)'),
  }

  const texteDoux = darkMode ? 'text-slate-400' : 'text-slate-500'
  const bordure = darkMode ? 'border-slate-800' : 'border-slate-200'
  const nbColonnes = 8 + colonnesProprietes.length

  const valeurPropriete = (p: ContactPropertyDef, v: unknown): string => {
    if (v === null || v === undefined || v === '') return ''
    if (p.kind === 'checkbox') return v ? t('contacts.suggestions.yes', 'Oui') : t('contacts.suggestions.no', 'Non')
    if (p.kind === 'date') {
      const d = new Date(String(v))
      return Number.isNaN(d.getTime()) ? String(v) : format(d, 'PP', { locale: dateLocale })
    }
    if (p.kind === 'url') return String(v).replace(/^https?:\/\//, '').replace(/\/$/, '')
    return String(v)
  }

  return (
    <div className={cn('w-full h-full flex overflow-hidden', darkMode ? 'bg-slate-950' : 'bg-white')}>
      <div className={cn('flex-1 min-w-0 flex flex-col', fichePhone && 'hidden md:flex')}>
        {/* Barre d'outils */}
        <div className={cn('flex-shrink-0 border-b px-4 py-3 space-y-3', bordure, darkMode ? 'bg-slate-900' : 'bg-white')}>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className={cn('text-base font-semibold mr-1', darkMode ? 'text-white' : 'text-slate-900')}>
              {t('contacts.title', 'Contacts')}
            </h1>
            <span className={cn('text-sm tabular-nums', texteDoux, isLoading && 'invisible')}>
              {t('contacts.emptyState.count', '{{count}} contacts', { count: total })}
            </span>
            <div className="flex-1" />
            {estGestionnaire && !corbeille && (
              <Button size="sm" variant="outline" onClick={exporter} disabled={exportEnCours || total === 0}>
                {exportEnCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {t('contacts.table.export', 'Exporter')}
              </Button>
            )}
            {ouvrirImport && (
              <Button size="sm" variant="outline" onClick={ouvrirImport}>
                <Upload className="h-4 w-4" />
                {t('contacts.table.import', 'Importer')}
              </Button>
            )}
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <UserPlus className="h-4 w-4" />
              {t('contacts.new', 'Nouveau contact')}
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4', darkMode ? 'text-slate-500' : 'text-slate-400')} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('contacts.searchPlaceholder', 'Rechercher un contact...')}
                className={cn(
                  'w-full h-8 pl-9 pr-3 rounded-lg text-sm outline-none transition-colors',
                  darkMode
                    ? 'bg-slate-800 text-white placeholder:text-slate-500 focus:bg-slate-700'
                    : 'bg-slate-100 text-slate-900 placeholder:text-slate-400 focus:bg-slate-200',
                )}
              />
            </div>
            {!corbeille && (
              <>
                <MenuFiltre
                  darkMode={darkMode}
                  libelle={t('contacts.labels.tags', 'Étiquettes')}
                  valeur={tag}
                  options={(facets?.tags ?? []).map((f) => ({ ...f, label: f.value }))}
                  onChange={setTag}
                />
                <MenuFiltre
                  darkMode={darkMode}
                  libelle={t('contacts.labels.status', 'Statut')}
                  valeur={status}
                  options={(facets?.statuses ?? []).map((f) => ({ ...f, label: libelleStatut(f.value) }))}
                  onChange={setStatus}
                />
                <MenuFiltre
                  darkMode={darkMode}
                  libelle={t('contacts.labels.source', 'Origine')}
                  valeur={source}
                  options={(facets?.sources ?? []).map((f) => ({ ...f, label: libelleSource(f.value) }))}
                  onChange={setSource}
                />
              </>
            )}
            <button
              type="button"
              onClick={() => setVue((v) => (v === 'archived' ? 'active' : 'archived'))}
              aria-pressed={vue === 'archived'}
              className={cn(pastille(darkMode, vue === 'archived'))}
            >
              <Archive className="h-3.5 w-3.5" />
              {t('contacts.table.archived', 'Archivés')}
              {!!facets?.archived && <span className="tabular-nums opacity-70">{facets.archived}</span>}
            </button>
            {estGestionnaire && (
              <button
                type="button"
                onClick={() => setVue((v) => (v === 'deleted' ? 'active' : 'deleted'))}
                aria-pressed={corbeille}
                className={cn(pastille(darkMode, corbeille))}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('contacts.table.trash', 'Corbeille')}
                {!!facets?.deleted && <span className="tabular-nums opacity-70">{facets.deleted}</span>}
              </button>
            )}
            {filtresActifs && (
              <button type="button" onClick={effacerFiltres} className={cn('text-xs font-medium px-2 h-8', darkMode ? 'text-indigo-400' : 'text-indigo-600')}>
                {t('contacts.table.clearFilters', 'Effacer les filtres')}
              </button>
            )}
            <div className="flex-1" />
            {!corbeille && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={pastille(darkMode, false)}>
                  <ArrowUpDown className="h-3.5 w-3.5" />
                  {trisLibelles[tri]}
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuRadioGroup value={tri} onValueChange={(v) => choisirTri(v as ContactsSort)}>
                  {(Object.keys(trisLibelles) as ContactsSort[]).map((k) => (
                    <DropdownMenuRadioItem key={k} value={k}>
                      {trisLibelles[k]}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </div>

        {/* Barre de sélection */}
        {nombreChoisi > 0 && (
          <BarreSelection
            darkMode={darkMode}
            nombre={nombreChoisi}
            vue={vue}
            estGestionnaire={estGestionnaire}
            enCours={bulk.isPending}
            suggestions={facets?.tags ?? []}
            etiquettesSelection={toutLeFiltre ? (facets?.tags ?? []).map((f) => f.value) : etiquettesSelection}
            libelleStatut={libelleStatut}
            // Every loaded row is checked but the filter holds more: offer them all, like Gmail.
            proposerTout={!toutLeFiltre && toutCoche && total > selection.size ? total : 0}
            toutLeFiltre={toutLeFiltre}
            onToutLeFiltre={() => setToutLeFiltre(true)}
            onAction={agir}
            onArchiver={() => setConfirmerArchive(true)}
            onSupprimer={() => setConfirmerSuppression(true)}
            onVider={viderSelection}
          />
        )}

        {/* Tableau */}
        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : contacts.length === 0 ? (
            <EtatVide
              darkMode={darkMode}
              cas={
                filtresActifs
                  ? 'aucunResultat'
                  : vue === 'archived'
                    ? 'aucunArchive'
                    : corbeille
                      ? 'corbeilleVide'
                      : 'carnetVide'
              }
              agent={!estGestionnaire}
              onEffacer={effacerFiltres}
              onNew={() => setAddOpen(true)}
              onImport={ouvrirImport}
            />
          ) : (
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className={cn(entete(darkMode), 'w-10 pl-4')}>
                    <input
                      type="checkbox"
                      checked={toutLeFiltre || toutCoche}
                      onChange={basculerTout}
                      aria-label={t('contacts.table.selectAll', 'Tout sélectionner')}
                      className="h-4 w-4 accent-indigo-600 cursor-pointer align-middle"
                    />
                  </th>
                  <th className={entete(darkMode)}>{t('contacts.table.name', 'Nom')}</th>
                  <th className={entete(darkMode)}>{t('contacts.labels.company', 'Entreprise')}</th>
                  <th className={entete(darkMode)}>{t('contacts.labels.phone', 'Téléphone')}</th>
                  <th className={entete(darkMode)}>{t('contacts.labels.tags', 'Étiquettes')}</th>
                  <th className={entete(darkMode)}>{t('contacts.labels.status', 'Statut')}</th>
                  <th className={entete(darkMode)}>
                    {corbeille
                      ? t('contacts.table.erasedIn', 'Effacé pour de bon')
                      : t('contacts.profile.lastContact', 'Dernier contact')}
                  </th>
                  <th className={entete(darkMode)}>{t('contacts.labels.source', 'Origine')}</th>
                  {colonnesProprietes.map((p) => (
                    <th key={p.id} className={entete(darkMode)}>
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => {
                  const coche = toutLeFiltre || selection.has(c.phone_number)
                  const ouverte = c.phone_number === fichePhone
                  const nom = nomLigne(c)
                  const photo = c.avatar_signed_url || c.avatar_url
                  const personne = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company_name?.trim() || ''
                  const etiquettes = c.tags || []
                  const cellule = cn('px-3 py-2 border-b align-middle', bordure)
                  return (
                    <tr
                      key={c.phone_number}
                      // A deleted contact has no card any more: a click checks the row.
                      onClick={() => (corbeille ? basculer(c.phone_number) : setFichePhone(c.phone_number))}
                      className={cn(
                        'cursor-pointer transition-colors',
                        ouverte || coche
                          ? darkMode
                            ? 'bg-slate-800/60'
                            : 'bg-indigo-50/60'
                          : darkMode
                            ? 'hover:bg-slate-900'
                            : 'hover:bg-slate-50',
                      )}
                    >
                      <td className={cn(cellule, 'pl-4 w-10')} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={coche}
                          onChange={() => basculer(c.phone_number)}
                          aria-label={nom}
                          className="h-4 w-4 accent-indigo-600 cursor-pointer align-middle"
                        />
                      </td>
                      <td className={cn(cellule, 'max-w-[260px]')}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          {photo ? (
                            <img src={photo} alt="" loading="lazy" className="h-8 w-8 rounded-full object-cover shrink-0" />
                          ) : (
                            <div
                              className={cn(
                                'h-8 w-8 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold select-none',
                                darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700',
                              )}
                            >
                              {personne ? initiales(personne) : <User className="h-4 w-4 opacity-60" />}
                            </div>
                          )}
                          <span className={cn('truncate font-medium', darkMode ? 'text-white' : 'text-slate-900')}>{nom}</span>
                        </div>
                      </td>
                      <td className={cn(cellule, 'max-w-[200px] truncate', darkMode ? 'text-slate-300' : 'text-slate-700')}>
                        {c.company_name || ''}
                      </td>
                      <td className={cn(cellule, 'whitespace-nowrap tabular-nums', darkMode ? 'text-slate-300' : 'text-slate-700')}>
                        {formatPhoneDisplay(c.phone_number)}
                      </td>
                      <td className={cellule}>
                        <div className="flex items-center gap-1 flex-nowrap">
                          {etiquettes.slice(0, 3).map((x) => (
                            <span
                              key={x}
                              className={cn(
                                'px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap max-w-[120px] truncate',
                                darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700',
                              )}
                            >
                              {x}
                            </span>
                          ))}
                          {etiquettes.length > 3 && (
                            <span className={cn('text-[11px] font-medium', texteDoux)} title={etiquettes.slice(3).join(', ')}>
                              +{etiquettes.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={cellule}>
                        {c.status && (
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap',
                              COULEUR_STATUT[c.status] ?? COULEUR_STATUT.inactive,
                            )}
                          >
                            {libelleStatut(c.status)}
                          </span>
                        )}
                      </td>
                      <td className={cn(cellule, 'whitespace-nowrap', texteDoux)}>
                        {corbeille
                          ? c.purge_at && (
                              <span title={format(new Date(c.purge_at), 'PPP', { locale: dateLocale })}>
                                {formatDistanceToNow(new Date(c.purge_at), { addSuffix: true, locale: dateLocale })}
                              </span>
                            )
                          : c.last_interaction
                            ? formatDistanceToNow(new Date(c.last_interaction), { addSuffix: true, locale: dateLocale })
                            : t('contacts.table.never', 'Jamais')}
                      </td>
                      <td className={cn(cellule, 'whitespace-nowrap', texteDoux)}>
                        {c.source && c.source !== 'unknown' ? libelleSource(c.source) : ''}
                      </td>
                      {colonnesProprietes.map((p) => (
                        <td key={p.id} className={cn(cellule, 'max-w-[180px] truncate', darkMode ? 'text-slate-300' : 'text-slate-700')}>
                          {valeurPropriete(p, c.custom_fields?.[String(p.id)])}
                        </td>
                      ))}
                    </tr>
                  )
                })}
                <tr ref={sentinelRef}>
                  <td colSpan={nbColonnes} className="py-3 text-center">
                    {isFetchingNextPage ? (
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400 inline-block" />
                    ) : hasNextPage ? (
                      <button type="button" onClick={() => fetchNextPage()} className={cn('text-xs font-medium', darkMode ? 'text-indigo-400' : 'text-indigo-600')}>
                        {t('contacts.table.loadMore', 'Charger plus')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Fiche du contact */}
      {fichePhone && (
        <div className={cn('w-full md:w-[360px] flex-shrink-0 flex flex-col md:border-l min-h-0', bordure)}>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <ContactProfile
              key={fichePhone}
              phoneNumber={fichePhone}
              darkMode={darkMode}
              onCall={(num) => void makeOutboundCall(num, selectedCallerId || undefined)}
              onOpenConversation={() => ouvrirConversation(fichePhone)}
              onClose={() => setFichePhone(null)}
            />
          </div>
        </div>
      )}

      <AlertDialog open={confirmerArchive} onOpenChange={setConfirmerArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.table.bulk.archiveTitle', { count: nombreChoisi })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'contacts.table.bulk.archiveBody',
                'Ils seront masqués du carnet. Un contact qui rappelle ou envoie un texto est restauré automatiquement.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Annuler')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => agir('archive')}>{t('contacts.table.bulk.archive', 'Archiver')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmerSuppression} onOpenChange={setConfirmerSuppression}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.table.bulk.deleteTitle', { count: nombreChoisi })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contacts.table.bulk.deleteBody', { count: nombreChoisi })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Annuler')}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 text-white hover:bg-red-700" onClick={() => agir('delete')}>
              {t('contacts.table.bulk.deleteConfirm', { count: nombreChoisi })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddContactDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(phone) => {
          // Le nouveau contact s'ouvre dans la Boîte de réception, fiche en édition.
          setProfilePanelCollapsed(false)
          setProfileToEditPhone(phone)
          ouvrirConversation(phone)
        }}
      />
      {estGestionnaire && <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} />}
    </div>
  )
}

function entete(darkMode: boolean) {
  return cn(
    'sticky top-0 z-10 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap border-b',
    darkMode ? 'bg-slate-900 text-slate-400 border-slate-800' : 'bg-slate-50 text-slate-500 border-slate-200',
  )
}

function pastille(darkMode: boolean, actif: boolean) {
  return cn(
    'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors',
    actif
      ? darkMode
        ? 'border-indigo-500/40 bg-indigo-500/15 text-indigo-300'
        : 'border-indigo-200 bg-indigo-50 text-indigo-700'
      : darkMode
        ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
        : 'border-slate-200 text-slate-700 hover:bg-slate-50',
  )
}

/** Un filtre du carnet : « Tous » ou une valeur, avec son nombre de contacts. */
function MenuFiltre({
  darkMode,
  libelle,
  valeur,
  options,
  onChange,
}: {
  darkMode: boolean
  libelle: string
  valeur: string
  options: (ContactFacet & { label: string })[]
  onChange: (v: string) => void
}) {
  const { t } = useTranslation()
  const choisie = options.find((o) => o.value === valeur)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={pastille(darkMode, !!valeur)}>
          {valeur ? `${libelle} : ${choisie?.label ?? valeur}` : libelle}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
        <DropdownMenuRadioGroup value={valeur} onValueChange={onChange}>
          <DropdownMenuRadioItem value="">{t('contacts.table.all', 'Tous')}</DropdownMenuRadioItem>
          {options.length > 0 && <DropdownMenuSeparator />}
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value}>
              <span className="flex-1 truncate">{o.label}</span>
              <span className="ml-3 text-xs tabular-nums opacity-60">{o.count}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Actions sur les contacts cochés. */
function BarreSelection({
  darkMode,
  nombre,
  vue,
  estGestionnaire,
  enCours,
  suggestions,
  etiquettesSelection,
  libelleStatut,
  proposerTout,
  toutLeFiltre,
  onToutLeFiltre,
  onAction,
  onArchiver,
  onSupprimer,
  onVider,
}: {
  darkMode: boolean
  nombre: number
  vue: ContactsView
  estGestionnaire: boolean
  enCours: boolean
  suggestions: ContactFacet[]
  etiquettesSelection: string[]
  libelleStatut: (v: string) => string
  /** Total of the filter to offer "Select all N contacts", 0 when not offered. */
  proposerTout: number
  toutLeFiltre: boolean
  onToutLeFiltre: () => void
  onAction: (action: BulkContactsAction, value?: string) => void
  onArchiver: () => void
  onSupprimer: () => void
  onVider: () => void
}) {
  const { t } = useTranslation()
  const [ajout, setAjout] = useState(false)
  const [texte, setTexte] = useState('')
  const actifs = vue === 'active'
  const valider = (valeur: string) => {
    const v = valeur.trim()
    if (!v) return
    onAction('add_tag', v)
    setAjout(false)
    setTexte('')
  }
  const proposees = suggestions
    .map((s) => s.value)
    .filter((v) => !texte.trim() || v.toLowerCase().includes(texte.trim().toLowerCase()))
    .slice(0, 8)
  const bouton = cn(
    'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50',
    darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-white',
  )
  const lien = cn('font-semibold underline-offset-2 hover:underline', darkMode ? 'text-indigo-300' : 'text-indigo-700')

  return (
    <div className={cn('flex-shrink-0 border-b px-4 py-2', darkMode ? 'bg-slate-800/60 border-slate-800' : 'bg-indigo-50/70 border-slate-200')}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={cn('text-sm font-semibold mr-2 tabular-nums', darkMode ? 'text-white' : 'text-slate-900')}>
          {t('contacts.table.selected', { count: nombre })}
        </span>
        {enCours && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        {actifs && (
          <>
            <button type="button" className={bouton} disabled={enCours} onClick={() => setAjout((a) => !a)}>
              <Tag className="h-3.5 w-3.5" />
              {t('contacts.table.bulk.addTag', 'Ajouter une étiquette')}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={bouton} disabled={enCours || etiquettesSelection.length === 0}>
                  <X className="h-3.5 w-3.5" />
                  {t('contacts.table.bulk.removeTag', 'Retirer une étiquette')}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                {etiquettesSelection.map((x) => (
                  <DropdownMenuItem key={x} onSelect={() => onAction('remove_tag', x)}>
                    {x}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={bouton} disabled={enCours}>
                  <Check className="h-3.5 w-3.5" />
                  {t('contacts.table.bulk.setStatus', 'Changer le statut')}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {STATUTS.map((s) => (
                  <DropdownMenuItem key={s} onSelect={() => onAction('set_status', s)}>
                    {libelleStatut(s)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {estGestionnaire && (
              <button type="button" className={bouton} disabled={enCours} onClick={onArchiver}>
                <Archive className="h-3.5 w-3.5" />
                {t('contacts.table.bulk.archive', 'Archiver')}
              </button>
            )}
          </>
        )}
        {vue === 'archived' && estGestionnaire && (
          <button type="button" className={bouton} disabled={enCours} onClick={() => onAction('restore')}>
            <ArchiveRestore className="h-3.5 w-3.5" />
            {t('contacts.table.bulk.restore', 'Restaurer')}
          </button>
        )}
        {vue !== 'deleted' && estGestionnaire && (
          <button
            type="button"
            className={cn(bouton, darkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50')}
            disabled={enCours}
            onClick={onSupprimer}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('contacts.table.bulk.delete', 'Supprimer')}
          </button>
        )}
        {vue === 'deleted' && estGestionnaire && (
          <button type="button" className={bouton} disabled={enCours} onClick={() => onAction('undelete')}>
            <RotateCcw className="h-3.5 w-3.5" />
            {t('contacts.table.bulk.restore', 'Restaurer')}
          </button>
        )}
        <div className="flex-1" />
        <button type="button" className={bouton} onClick={onVider}>
          {t('contacts.table.bulk.clear', 'Désélectionner')}
        </button>
      </div>

      {(proposerTout > 0 || toutLeFiltre) && (
        <p className={cn('mt-1.5 text-xs', darkMode ? 'text-slate-300' : 'text-slate-600')}>
          {toutLeFiltre ? (
            t('contacts.table.allSelected', { count: nombre })
          ) : (
            <>
              {t('contacts.table.loadedSelected', { count: nombre })}{' '}
              <button type="button" className={lien} onClick={onToutLeFiltre}>
                {t('contacts.table.selectAllMatching', { count: proposerTout })}
              </button>
            </>
          )}
        </p>
      )}

      {ajout && actifs && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <input
            autoFocus
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') valider(texte)
              if (e.key === 'Escape') setAjout(false)
            }}
            placeholder={t('contacts.table.bulk.tagPlaceholder', 'Nom de l’étiquette')}
            className={cn(
              'h-7 w-48 px-2.5 rounded-md text-xs outline-none border',
              darkMode ? 'bg-slate-900 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400',
            )}
          />
          <Button size="sm" className="h-7 text-xs" disabled={!texte.trim()} onClick={() => valider(texte)}>
            {t('contacts.table.bulk.apply', 'Ajouter')}
          </Button>
          {proposees.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => valider(v)}
              className={cn(
                'h-6 px-2 rounded text-[11px] font-medium',
                darkMode ? 'bg-slate-900 text-slate-300 hover:bg-slate-700' : 'bg-white text-slate-700 hover:bg-slate-100',
              )}
            >
              {v}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** Tableau vide : carnet vide, aucun archivé, corbeille vide, ou filtres sans résultat. */
function EtatVide({
  darkMode,
  cas,
  agent,
  onEffacer,
  onNew,
  onImport,
}: {
  darkMode: boolean
  cas: 'carnetVide' | 'aucunArchive' | 'corbeilleVide' | 'aucunResultat'
  agent: boolean
  onEffacer: () => void
  onNew: () => void
  onImport?: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-center p-6 py-20">
      <div className="text-center max-w-sm">
        <Users className={cn('h-10 w-10 mx-auto mb-4', darkMode ? 'text-slate-600' : 'text-slate-300')} />
        {cas === 'aucunResultat' ? (
          <>
            <h2 className={cn('text-base font-semibold', darkMode ? 'text-white' : 'text-slate-900')}>
              {t('contacts.table.noMatch', 'Aucun contact ne correspond')}
            </h2>
            <div className="mt-5">
              <Button size="sm" variant="outline" onClick={onEffacer}>
                {t('contacts.table.clearFilters', 'Effacer les filtres')}
              </Button>
            </div>
          </>
        ) : cas === 'aucunArchive' ? (
          <h2 className={cn('text-base font-semibold', darkMode ? 'text-white' : 'text-slate-900')}>
            {t('contacts.table.noArchived', 'Aucun contact archivé')}
          </h2>
        ) : cas === 'corbeilleVide' ? (
          <>
            <h2 className={cn('text-base font-semibold', darkMode ? 'text-white' : 'text-slate-900')}>
              {t('contacts.table.trashEmpty', 'La corbeille est vide')}
            </h2>
            <p className={cn('mt-1.5 text-sm', darkMode ? 'text-slate-400' : 'text-slate-500')}>
              {t('contacts.table.trashEmptyBody', 'Un contact supprimé y reste 30 jours, puis il est effacé pour de bon.')}
            </p>
          </>
        ) : (
          <>
            <h2 className={cn('text-base font-semibold', darkMode ? 'text-white' : 'text-slate-900')}>
              {t('contacts.table.emptyTitle', 'Le carnet est vide')}
            </h2>
            <p className={cn('mt-1.5 text-sm', darkMode ? 'text-slate-400' : 'text-slate-500')}>
              {agent
                ? t('contacts.table.emptyBodyAgent', 'Ajoutez un contact, ou il sera créé au premier appel.')
                : t('contacts.table.emptyBody', 'Importez votre liste de clients depuis un fichier CSV ou Excel, ou ajoutez un contact.')}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {onImport && (
                <Button size="sm" onClick={onImport}>
                  <Upload className="h-4 w-4" />
                  {t('contacts.import.menu', 'Importer un fichier')}
                </Button>
              )}
              <Button size="sm" variant={onImport ? 'outline' : 'default'} onClick={onNew}>
                <UserPlus className="h-4 w-4" />
                {t('contacts.new', 'Nouveau contact')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
